/**
 * Shared protocol + config types for the Vyro chat embed.
 *
 * The loader (this package, running on the *host* page) and the chat iframe
 * (modern-website `/iframes/chat`, running on the *Vyro embeds host*) talk over
 * `window.postMessage`. Every message is a JSON string of {@link VyroChatMessage}.
 *
 * Codes are namespaced (`vyro:chat:*`) so they never collide with the existing
 * forms embed loader, which uses the bare codes `height` / `redirect`.
 */
export const MESSAGE_SOURCE = 'vyro' as const

export type ChatMessageCode =
  | 'vyro:chat:ready'
  | 'vyro:chat:resize'
  | 'vyro:chat:event'
  | 'vyro:chat:context'
  | 'vyro:chat:viewport'

export interface VyroChatMessage<TPayload = unknown> {
  source: typeof MESSAGE_SOURCE
  code: ChatMessageCode
  payload: TPayload
}

/** iframe -> loader: how large the iframe should be for the current widget state. */
export interface ResizePayload {
  state: 'collapsed' | 'expanded'
  width: number
  height: number
}

/** iframe -> loader: an analytics event to forward to the host page's dataLayer. */
export interface EventPayload {
  name: string
  data?: Record<string, unknown>
}

/**
 * loader -> iframe: the HOST page's viewport. The iframe cannot measure this
 * itself — `window.innerWidth/Height` inside the iframe report the iframe's own
 * (loader-constrained) size, not the page's — so the loader, which runs on the
 * host page, is the source of truth for sizing and mobile/desktop layout.
 */
export interface ViewportPayload {
  width: number
  height: number
  isMobile: boolean
}

/** loader -> iframe: host-page context so the widget can be page-aware. */
export interface ContextPayload {
  url: string
  title: string
  referrer: string
  vehicleId: string | null
  vehicleCode: string | null
}

export type EmbedPosition = 'bottom-right' | 'bottom-left'

/** Resolved configuration read from the embedding `<script>` tag. */
export interface EmbedConfig {
  /** Showroom code, e.g. "giltrap". */
  showroom: string
  /** Origin serving the iframe route (the Vyro embeds host), no trailing slash. */
  host: string
  position: EmbedPosition
}
